import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';
import {
    TextControl,
    SelectControl,
    Spinner,
    FormTokenField,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useEffect } from '@wordpress/element';

const GroupSettings = ({ groupData, setGroupData }) => {
    const postTypes = useSelect(
        (select) => select(coreStore).getPostTypes({ per_page: -1 }),
        []
    );
    const isLoading = !postTypes;

    const filteredPostTypes = (postTypes || []).filter(
        (pt) =>
            pt.viewable &&
            pt.slug !== 'attachment' &&
            pt.slug !== 'voxy-components'
    );

    const options = [
        { value: 'post-settings', label: __('Post Settings', 'voxycure-framework') },
        { value: 'block', label: __('As Block', 'voxycure-framework') },
        { value: 'product', label: __('Product', 'voxycure-framework') },
    ];

    // Set default group type
    if (!isLoading && !groupData.type) {
        setGroupData({ ...groupData, type: 'post-settings' });
    }

    // Get available blocks
    const blocks = (window.VoxyFrameAdmin?.blocks || []).map((block) => ({
        block_id: block.id,
        label: block.label,
    }));

    // Ensure selected block_id is valid when in 'block' mode
    useEffect(() => {
        if (groupData.type === 'block') {
            if (!groupData.block_id || groupData.block_id === '') {
                if (blocks.length > 0) {
                    setGroupData({ ...groupData, block_id: blocks[0].block_id });
                }
            } else if (!blocks.some((blk) => blk.block_id === groupData.block_id)) {
                if (blocks.length > 0) {
                    setGroupData({ ...groupData, block_id: blocks[0].block_id });
                } else {
                    setGroupData({ ...groupData, block_id: '' });
                }
            }
        }
    }, [groupData.type, groupData.block_id, blocks, setGroupData]);

    return (
        <>
            <TextControl
                label={__('Group Name', 'voxycure-framework')}
                value={groupData.name}
                onChange={(value) => setGroupData({ ...groupData, name: value })}
                required
            />

            {isLoading ? (
                <Spinner />
            ) : (
                <SelectControl
                    label={__('Group Type', 'voxycure-framework')}
                    value={groupData.type}
                    options={options}
                    onChange={(value) => setGroupData({ ...groupData, type: value })}
                    required
                />
            )}

            {groupData.type === 'post-settings' && (
                <FormTokenField
                    label={__('Select Post Types', 'voxycure-framework')}
                    value={groupData.post_types || []}
                    suggestions={filteredPostTypes.map((pt) => pt.slug)}
                    onChange={(value) => setGroupData({ ...groupData, post_types: value })}
                />
            )}

            {groupData.type === 'block' && blocks.length > 0 && (
                <SelectControl
                    label={__('Block', 'voxycure-framework')}
                    value={groupData.block_id || ''}
                    options={blocks.map((blk) => ({ value: blk.block_id, label: blk.label }))}
                    onChange={(value) => setGroupData({ ...groupData, block_id: value })}
                    required
                />
            )}

            {groupData.type === 'product' && (
                <>
                    <SelectControl
                        label={__('Product Section', 'voxycure-framework')}
                        value={groupData.section || ''}
                        options={[
                            { value: 'general', label: __('General', 'voxycure-framework') },
                            { value: 'inventory', label: __('Inventory', 'voxycure-framework') },
                            { value: 'linked', label: __('Linked Products', 'voxycure-framework') },
                            { value: 'shipping', label: __('Shipping', 'voxycure-framework') },
                            { value: 'variations', label: __('Variations', 'voxycure-framework') },
                        ]}
                        onChange={(value) => setGroupData({ ...groupData, section: value })}
                        required
                    />

                    <TextControl
                        label={__('Priority', 'voxycure-framework')}
                        type="number"
                        value={groupData.priority}
                        onChange={(value) =>
                            setGroupData({ ...groupData, priority: parseInt(value) || '' })
                        }
                        min={1}
                    />
                </>
            )}
        </>
    );
};

export default GroupSettings;
